/* eslint-disable import/order */
/* eslint-disable camelcase */

import { sentenceCase } from 'change-case';
import { useState, useEffect } from 'react';

import {
  Card,
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton
} from '@mui/material';

import { useTheme } from '@mui/material/styles';

import axios, { getErrorMessage } from 'utils/axios';
import useIsMountedRef from 'hooks/useIsMountedRef';
import CRUD from 'utils/Crud';

import { PATH_DASHBOARD } from '../../routes/paths';
import useSettings from '../../hooks/useSettings';
import { useSnackbar } from 'notistack';

import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';

import { DataGrid } from '@mui/x-data-grid';
import { LoadingButton } from '@mui/lab';

import CustomGridToolbar from 'components/CustomGridToolbar';
import ProfileDialog from 'sections/@dashboard/user/ProfileDialog';
import Label from 'components/Label';
import UserKYCDialog from 'sections/@dashboard/user/UserKYCDialog';

export default function ComplianceHistory() {
  const { themeStretch } = useSettings();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [filters, setFilters] = useState({
    page: 1,
    page_size: 50,
    stage: '',
    risk_level: '',
    user_id: ''
  });

  const [data, setData] = useState({
    data: [],
    current_page: 1,
    total_size: 0,
    page_size: 50
  });

  const [state, setState] = useState({
    loading: false,
    error: null
  });

  const [profileDialog, setProfileDialog] = useState({ open: false, user: null });
  const [kyc, setKyc] = useState({ open: false, userId: null });

  useEffect(() => {
    getInitData(1);
  }, []);

  async function getInitData(page = 1) {
    setState({ loading: true, error: null });

    try {
      const query =
        `?page=${page}` +
        `&page_size=${filters.page_size}` +
        `&stage=${filters.stage}` +
        `&risk_level=${filters.risk_level}` +
        `&user_id=${filters.user_id}`;

      const res = await axios.get(CRUD.compliance.history + query);

      if (isMountedRef.current) {
        setData(res.data);
      }

      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: getErrorMessage(e) });
    }
  }

  function renderStatus(value, successValue) {
    let color = 'default';

    if (value === successValue) color = 'success';
    else if (value === 'pending') color = 'warning';
    else if (value === 'failed' || value === 'rejected') color = 'error';

    return <Label color={color}>{sentenceCase(value || 'N/A')}</Label>;
  }

  return (
    <Page title="Compliance History">
      <Container maxWidth={themeStretch ? false : 'lg'}>

        <HeaderBreadcrumbs
          heading="Compliance History"
          links={[
            { name: 'Compliance', href: PATH_DASHBOARD.compliance.root },
            { name: 'History' },
          ]}
        />

        <Card>

          {/* FILTERS */}
          <Box sx={{ display: "flex", gap: 2, p: 2, flexWrap: 'wrap' }}>

            <TextField
              label="User ID"
              size="small"
              value={filters.user_id}
              onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
            />

            <FormControl sx={{ width: 180 }}>
              <InputLabel>Stage</InputLabel>
              <Select
                value={filters.stage}
                label="Stage"
                onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="stage_one">Stage 1</MenuItem>
                <MenuItem value="stage_two">Stage 2</MenuItem>
                <MenuItem value="stage_three">Stage 3</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ width: 180 }}>
              <InputLabel>Risk</InputLabel>
              <Select
                value={filters.risk_level}
                label="Risk"
                onChange={(e) => setFilters({ ...filters, risk_level: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <LoadingButton
              loading={state.loading}
              onClick={() => getInitData(1)}
              variant="contained"
            >
              Apply
            </LoadingButton>
          </Box>

          {/* TABLE */}
          <Box sx={{ height: 650 }}>
            <DataGrid
              rows={data.data}
              getRowId={(row) => row.session_id}

              columns={[
                {
                  field: 'user',
                  headerName: 'User',
                  width: 260,
                  renderCell: (params) => (
                    <Box display="flex" alignItems="center">
                      <IconButton
                        onClick={() =>
                          setProfileDialog({ open: true, user: { user_id: params.row.user_id } })
                        }
                      >
                        <Iconify icon={'ph:user'} />
                      </IconButton>

                      <Box ml={1}>
                        <Typography
                          variant="subtitle2"
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() =>
                            setKyc({ open: true, userId: params.row.user_id })
                          }
                        >
                          {params.row.first_name} {params.row.last_name}
                        </Typography>

                        <Typography variant="caption">
                          {params.row.user_id.slice(0, 10)}...
                        </Typography>
                      </Box>
                    </Box>
                  ),
                },

                {
                  field: 'kyc_tier',
                  headerName: 'Stage',
                  width: 130,
                },

                {
                  field: 'status',
                  headerName: 'Session',
                  width: 120,
                  renderCell: (params) =>
                    <Label color={params.value === 'completed' ? 'success' : 'warning'}>
                      {params.value}
                    </Label>
                },

                {
                  field: 'risk_level',
                  headerName: 'Risk',
                  width: 120,
                  renderCell: (params) =>
                    <Label color={
                      params.value === 'high' ? 'error' :
                      params.value === 'medium' ? 'warning' : 'success'
                    }>
                      {params.value}
                    </Label>
                },

                {
                  field: 'confidence_score',
                  headerName: 'Confidence',
                  width: 130,
                  renderCell: (params) =>
                    params.value ? `${Math.round(params.value)}%` : '—'
                },

                {
                  field: 'updated_at',
                  headerName: 'Updated',
                  width: 180,
                },
              ]}

              loading={state.loading}
              components={{ Toolbar: CustomGridToolbar }}

              paginationMode="server"
              rowCount={data.total_size}
              page={data.current_page - 1}
              pageSize={data.page_size}

              onPageChange={(newPage) => {
                const page = newPage + 1;
                setFilters((p) => ({ ...p, page }));
                getInitData(page);
              }}

              onPageSizeChange={(size) => {
                setFilters((p) => ({ ...p, page_size: size }));
                getInitData(1);
              }}
            />
          </Box>
        </Card>
      </Container>

      <UserKYCDialog
        open={kyc.open}
        userID={kyc.userId}
        handleClose={() => setKyc({ open: false, userId: null })}
      />

       {profileDialog.open && (
          <ProfileDialog
            user={profileDialog.user}
            refresh
            handleClose={() => setProfileDialog({ open: false })}
          />
        )}
    </Page>
  );
}